import { ArrowBackIcon } from "@chakra-ui/icons";
import { Box, Button, ButtonGroup, Flex, Link, Spinner, Tag, Text } from "@chakra-ui/react";
import { useRouter } from "@uirouter/react";
import { useQuery } from "@tanstack/react-query";
import Page from "../../shared/components/Page";
import useApi from "../../shared/hooks/useApi";
import { useEntityLink } from "../../shared/hooks/useEntityLink";
import { PatientId } from "../../shared/schema/schema";
import { dateFormatter } from "../../shared/utils/date-formatter";
import DailyNoteAttestationsTab from "./DailyNoteAttestationsRoute";
import DailyNotesTable from "./DailyNotesTable";
import { formatErrorResponse } from "../../shared/utils/format-response-error";

export type DailyNotePatientViewKind = "review" | "all" | "attestations";

/**
 * One patient's daily notes: what is waiting for a nurse, and the record behind it.
 *
 * What a nurse lands on when she opens a patient from the list, and what a History click opens
 * on a different view of the same page. A header says who and how much, and a switch moves
 * between three views of the same patient:
 *
 * - To review: the notes no attestation covers yet, with the RN Attestation action.
 * - All notes: the whole history, signed or not, read-only.
 * - Attestations: every batch signed for this patient, with its download.
 *
 * The view is in the URL, so a link or a refresh lands in the same place.
 */
export default function DailyNotePatientView(props: {
  patientId: PatientId;
  view: DailyNotePatientViewKind;
}) {
  const { queries } = useApi();
  const entityLink = useEntityLink();
  const { stateService } = useRouter();

  const summary = useQuery(queries.dailyNotes.patientSummary(props.patientId));

  const goTo = (view: DailyNotePatientViewKind) =>
    stateService.go("app.visits.dailyNotes", { notesPatient: props.patientId, notesView: view });

  const backToList = () =>
    stateService.go("app.visits.dailyNotes", { notesPatient: null, notesView: null });

  if (summary.isError) {
    // A patient who is not this agency's reads as not found, and a stale link should say so
    // rather than show an empty table that looks like "nothing to review".
    return (
      <Box>
        <Page.Content>
          <Button leftIcon={<ArrowBackIcon />} size="sm" variant="ghost" onClick={backToList}>
            All patients
          </Button>
          <Text color="red.700" mt={4}>
            {formatErrorResponse(summary.error)}
          </Text>
        </Page.Content>
      </Box>
    );
  }

  const data = summary.data;

  return (
    <Box>
      {/* One column, back link on top: Page.Header lays its children out in a wrapping row,
          which put the button beside the name instead of above it. */}
      <Page.Header direction="column" gap={2} pb={5}>
        <Button
          alignSelf="flex-start"
          leftIcon={<ArrowBackIcon />}
          size="sm"
          variant="ghost"
          onClick={backToList}
        >
          All patients
        </Button>

        {/* Counts sit right after the name, on the same row. */}
        <Flex align="center" gap={6} wrap="wrap">
          {data === undefined ? (
            <Spinner size="sm" />
          ) : (
            <Flex align="baseline" gap={3} wrap="wrap">
              <Page.Title>
                {/* The name is the way into the patient's profile, as it is everywhere else
                    in the product. */}
                <Link
                  onClick={(event) =>
                    entityLink.open({
                      event,
                      entity: { type: "Patient", id: props.patientId },
                    })
                  }
                >
                  {data.patientName}
                </Link>
              </Page.Title>
              {data.patientExternalId === null ? null : (
                <Text color="gray.500" fontSize="sm">
                  ID {data.patientExternalId}
                </Text>
              )}
            </Flex>
          )}

          {data === undefined ? null : (
            <Flex align="center" gap={2} wrap="wrap">
              {data.urgentCount > 0 ? (
                <Tag colorScheme="red" size="md">
                  {data.urgentCount} urgent
                </Tag>
              ) : null}
              {data.highCount > 0 ? (
                <Tag colorScheme="orange" size="md">
                  {data.highCount} high
                </Tag>
              ) : null}
              {data.dueCount > 0 ? (
                <Tag colorScheme="red" size="md" variant="outline">
                  {data.dueCount} due
                </Tag>
              ) : null}
              {data.unclassifiedCount > 0 ? (
                <Tag size="md" variant="outline">
                  {data.unclassifiedCount} analysing
                </Tag>
              ) : null}
              {data.lastSignedAt === null ? null : (
                <Text color="gray.600" fontSize="sm">
                  Last signed {dateFormatter.toDateTime(data.lastSignedAt)}
                </Text>
              )}
            </Flex>
          )}
        </Flex>
      </Page.Header>

      <Page.Content pt={1}>
        {/* The counts ride on the labels so the three views read as parts of one record. */}
        <ButtonGroup isAttached={true} mb={3} size="sm" variant="outline">
          <Button
            colorScheme={props.view === "review" ? "blue" : "gray"}
            variant={props.view === "review" ? "solid" : "outline"}
            onClick={() => goTo("review")}
          >
            To review{data === undefined ? "" : ` (${data.unsignedNoteCount})`}
          </Button>
          <Button
            colorScheme={props.view === "all" ? "blue" : "gray"}
            variant={props.view === "all" ? "solid" : "outline"}
            onClick={() => goTo("all")}
          >
            All notes{data === undefined ? "" : ` (${data.totalNoteCount})`}
          </Button>
          <Button
            colorScheme={props.view === "attestations" ? "blue" : "gray"}
            variant={props.view === "attestations" ? "solid" : "outline"}
            onClick={() => goTo("attestations")}
          >
            Attestations{data === undefined ? "" : ` (${data.attestationCount})`}
          </Button>
        </ButtonGroup>

        {props.view === "attestations" ? (
          <DailyNoteAttestationsTab patientId={props.patientId} />
        ) : (
          // Keyed on the mode as well as the patient, so switching view starts the table
          // fresh -- its filters and selection belong to one view, not both.
          <DailyNotesTable
            key={`${props.patientId}-${props.view}`}
            mode={props.view}
            patientId={props.patientId}
          />
        )}
      </Page.Content>
    </Box>
  );
}
